<template>
  <div class="alice-er">
    <!-- ============== Sub-page header ============== -->
    <header class="alice-er__head">
      <div class="alice-er__head-main">
        <div class="alice-er__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Eerder onderzoek</span>
        </div>
        <h1 class="alice-er__title">Eerder onderzoek</h1>
        <p class="alice-er__subtitle">
          Wat Valsplat eerder deed voor deze klant en over dit onderwerp, uit
          het archief (2008–nu) en Alice. Gebruik het als context: bewijs voor
          dit project komt uit de eigen sessies.
        </p>
      </div>
      <div v-if="!isGuest" class="alice-er__head-actions">
        <button
          class="alice-er__head-btn"
          type="button"
          title="Opent de chat met een vergelijking met de vorige ronde. Er wordt niets verstuurd."
          @click="comparePreviousRound"
        >
          <Icon name="layers" :size="16" />
          <span>Vergelijk met vorige ronde</span>
        </button>
        <button
          class="alice-er__head-btn"
          type="button"
          title="Opent de chat met de skill create-testscript. Er wordt niets verstuurd."
          @click="testscriptBlocks"
        >
          <Icon name="file" :size="16" />
          <span>Bouwstenen testscript</span>
        </button>
      </div>
    </header>

    <div v-if="isGuest" class="alice-er__empty">
      Eerder onderzoek is alleen beschikbaar voor het Valsplat-team.
    </div>

    <div v-else class="alice-er__body">
      <p v-for="n in notes" :key="n" class="alice-er__note">{{ n }}</p>

      <!-- ============== This study and its rounds ============== -->
      <section class="alice-er__section">
        <header class="alice-er__section-head">
          <span
            class="alice-er__section-mark"
            style="background: var(--vp-turquoise-100)"
          />
          <h2 class="alice-er__section-title">Deze studie en haar rondes</h2>
          <p class="alice-er__section-sub">
            Dezelfde studie in het archief of in Alice, en eerdere en latere
            rondes, met de reden waarom ze bij elkaar horen.
          </p>
        </header>
        <div v-if="loadingMain" class="alice-er__loading">
          <ThinkingDots :size="5" />
          <span>Projectcatalogus doorzoeken…</span>
        </div>
        <div v-else-if="errorMain" class="alice-er__error">
          <span>{{ errorMain }}</span>
          <button class="alice-er__retry" type="button" @click="load()">
            Opnieuw
          </button>
        </div>
        <p v-else-if="related && related.error" class="alice-er__error">
          {{ related.error }}
        </p>
        <template v-else-if="related">
          <!-- The Drive study a colleague linked this project to. -->
          <EarlierResearchRow
            v-if="driveRow"
            :row="driveRow"
            badge="Gekoppeld aan Drive"
            detail="reason"
            @ask="askAbout"
          >
            <div class="alice-er-link">
              <span class="alice-er-link__text">{{ driveNote }}</span>
              <button
                class="alice-er-link__btn"
                type="button"
                :disabled="Boolean(linkBusy)"
                @click="unlinkDrive"
              >
                {{ linkBusy === "unlink" ? "Ontkoppelen…" : "Ontkoppelen" }}
              </button>
            </div>
            <p v-if="linkErrorFor === 'unlink'" class="alice-er-link__error">
              {{ linkError }}
            </p>
          </EarlierResearchRow>
          <EarlierResearchRow
            v-for="r in related.previous_rounds"
            :key="'p:' + r.id"
            :row="r"
            :badge="roundLabel(r, 'previous')"
            detail="reason"
            @ask="askAbout"
          />
          <EarlierResearchRow
            v-for="r in related.same_study"
            :key="'s:' + r.id"
            :row="r"
            :badge="r.possible ? 'Mogelijk zelfde studie' : 'Zelfde studie'"
            detail="reason"
            @ask="askAbout"
          >
            <!-- A suggestion: link it, or say it is not the same study. -->
            <div
              v-if="r.possible && canLink"
              class="alice-er-link alice-er-link--ask"
            >
              <span class="alice-er-link__text">
                Mogelijk dezelfde studie op Drive:
                <a
                  v-if="folderUrl(r)"
                  :href="folderUrl(r)"
                  target="_blank"
                  rel="noopener"
                  class="alice-er-link__folder"
                  title="Open de map op Drive"
                  >map <Icon name="external" :size="10"
                /></a>
              </span>
              <span class="alice-er-link__actions">
                <button
                  v-if="r.folder_id"
                  class="alice-er-link__btn alice-er-link__btn--yes"
                  type="button"
                  :disabled="Boolean(linkBusy)"
                  @click="confirmLink(r)"
                >
                  {{ linkBusy === r.id ? "Koppelen…" : "Ja, koppel" }}
                </button>
                <button
                  class="alice-er-link__btn"
                  type="button"
                  :disabled="Boolean(linkBusy)"
                  @click="rejectLink(r)"
                >
                  Nee, niet dezelfde
                </button>
              </span>
              <span v-if="!r.folder_id" class="alice-er-link__hint">
                Deze map staat niet in de Drive-sync; koppelen kan alleen met
                een link hieronder.
              </span>
            </div>
            <!-- An automatic link (job number, same file) can be wrong. -->
            <div v-else-if="r.rejectable && canLink" class="alice-er-link">
              <button
                class="alice-er-link__quiet"
                type="button"
                title="Alice koppelt dit project dan nooit meer automatisch aan deze Drive-map."
                :disabled="Boolean(linkBusy)"
                @click="rejectLink(r)"
              >
                {{ linkBusy === r.id ? "Opslaan…" : "Niet dezelfde studie" }}
              </button>
            </div>
            <p v-if="linkErrorFor === r.id" class="alice-er-link__error">
              {{ linkError }}
            </p>
          </EarlierResearchRow>
          <EarlierResearchRow
            v-for="r in related.next_rounds"
            :key="'n:' + r.id"
            :row="r"
            :badge="roundLabel(r, 'next')"
            detail="reason"
            @ask="askAbout"
          />
          <p v-if="!relatedCount" class="alice-er__none">
            Geen andere rondes of dezelfde studie gevonden in de
            projectcatalogus.
          </p>

          <!-- Turned down: says so, and can be taken back. -->
          <p
            v-for="x in rejectedList"
            :key="'x:' + x.archive_id"
            class="alice-er-link__undo"
          >
            <span
              >Niet dezelfde studie: <strong>{{ x.title }}</strong
              >. {{ SEARCH_FOLLOWS }}</span
            >
            <button
              class="alice-er-link__quiet"
              type="button"
              :disabled="Boolean(linkBusy)"
              @click="undoReject(x)"
            >
              {{
                linkBusy === "undo:" + x.archive_id
                  ? "Ongedaan maken…"
                  : "Ongedaan maken"
              }}
            </button>
          </p>
          <p v-if="linkErrorFor === 'undo'" class="alice-er-link__error">
            {{ linkError }}
          </p>

          <!-- Link it yourself: paste the Drive project folder. -->
          <div v-if="canLink && !driveRow" class="alice-er-paste">
            <button
              v-if="!pasteOpen"
              class="alice-er__more-btn"
              type="button"
              @click="openPaste"
            >
              Zelf koppelen aan een Drive-map
            </button>
            <form
              v-else
              class="alice-er-paste__form"
              @submit.prevent="pasteLink"
            >
              <label class="alice-er-paste__label" for="alice-er-drive-folder"
                >Link van de projectmap op Drive</label
              >
              <div class="alice-er-paste__row">
                <input
                  id="alice-er-drive-folder"
                  ref="pasteInput"
                  v-model="pasteValue"
                  class="alice-er-paste__input"
                  type="text"
                  placeholder="https://drive.google.com/drive/folders/…"
                  autocomplete="off"
                  spellcheck="false"
                />
                <button
                  class="alice-er-link__btn alice-er-link__btn--yes"
                  type="submit"
                  :disabled="Boolean(linkBusy) || !pasteValue.trim()"
                >
                  {{ linkBusy === "paste" ? "Koppelen…" : "Koppel" }}
                </button>
                <button
                  class="alice-er-link__btn"
                  type="button"
                  :disabled="linkBusy === 'paste'"
                  @click="closePaste"
                >
                  Annuleren
                </button>
              </div>
              <p class="alice-er-paste__hint">
                Een projectmap in de klantendrives, geen klantmap of losse
                bestanden. Je ziet de koppeling meteen hier.
                {{ SEARCH_FOLLOWS }}
              </p>
              <p v-if="linkErrorFor === 'paste'" class="alice-er-link__error">
                {{ linkError }}
              </p>
            </form>
          </div>
        </template>
      </section>

      <!-- ============== The client's history ============== -->
      <section class="alice-er__section">
        <header class="alice-er__section-head">
          <span
            class="alice-er__section-mark"
            style="background: var(--vp-geel-100)"
          />
          <h2 class="alice-er__section-title">
            {{
              history && history.client
                ? `Eerder voor ${history.client}`
                : "Eerder voor deze klant"
            }}
          </h2>
          <span v-if="historyCount" class="alice-er__count-pill">{{
            historyCount
          }}</span>
          <p class="alice-er__section-sub">
            Alles wat we voor deze klant deden, nieuwste eerst, ook onder andere
            namen van de klant.
          </p>
        </header>
        <div v-if="loadingMain" class="alice-er__loading">
          <ThinkingDots :size="5" />
          <span>Klanthistorie ophalen…</span>
        </div>
        <template v-else-if="history">
          <p v-if="history.error" class="alice-er__error">
            {{ history.error }}
          </p>
          <p v-else-if="!history.client" class="alice-er__none">
            Dit project heeft geen klant. Kies een klant onder Details, dan
            verschijnt hier de klanthistorie.
          </p>
          <p v-else-if="!history.rows.length" class="alice-er__none">
            Geen eerdere projecten voor {{ history.client }} gevonden. Is dit
            een nieuwe klant, kijk dan hieronder naar vergelijkbaar onderzoek.
          </p>
          <template v-else>
            <EarlierResearchRow
              v-for="r in history.rows"
              :key="'h:' + r.id"
              :row="r"
              @ask="askAbout"
            />
            <div v-if="history.total > history.shown" class="alice-er__more">
              <span
                >Nog {{ history.total - history.shown }}
                {{
                  history.total - history.shown === 1 ? "project" : "projecten"
                }}
                niet getoond.</span
              >
              <button
                class="alice-er__more-btn"
                type="button"
                @click="askFullHistory"
              >
                Volledige lijst in chat
              </button>
            </div>
          </template>
        </template>
      </section>

      <!-- ============== Similar studies elsewhere ============== -->
      <section class="alice-er__section">
        <header class="alice-er__section-head">
          <span
            class="alice-er__section-mark"
            style="background: var(--vp-blauw-120)"
          />
          <h2 class="alice-er__section-title">
            Vergelijkbaar onderzoek bij andere klanten
          </h2>
          <p class="alice-er__section-sub">
            Gezocht op de titel, de onderzoeksvragen en de projectkaart van dit
            project.
          </p>
        </header>
        <div v-if="loadingSimilar" class="alice-er__loading">
          <ThinkingDots :size="5" />
          <span>Vergelijkbaar onderzoek zoeken…</span>
        </div>
        <div v-else-if="errorSimilar" class="alice-er__error">
          <span>{{ errorSimilar }}</span>
          <button class="alice-er__retry" type="button" @click="load()">
            Opnieuw
          </button>
        </div>
        <template v-else-if="similar">
          <p v-if="similar.error" class="alice-er__error">
            {{ similar.error }}
          </p>
          <p v-else-if="!similar.seed" class="alice-er__none">
            Te weinig om op te zoeken. Vul onder Details de onderzoeksvragen in,
            dan zoekt Alice hier vergelijkbaar onderzoek.
          </p>
          <p v-else-if="!similar.rows.length" class="alice-er__none">
            Geen vergelijkbaar onderzoek bij andere klanten gevonden.
          </p>
          <template v-else>
            <EarlierResearchRow
              v-for="r in similar.rows"
              :key="'v:' + r.id"
              :row="r"
              @ask="askAbout"
            />
          </template>
          <p v-if="similar.seed" class="alice-er__seed">
            Gezocht op: “{{ similar.seed }}”
          </p>
        </template>
      </section>

      <footer v-if="builtLabel" class="alice-er__foot">
        Projectcatalogus bijgewerkt op {{ builtLabel }}. Rondes en dezelfde
        studie zijn automatisch gekoppeld; controleer ze voordat je ze gebruikt.
      </footer>
    </div>
  </div>
</template>

<script>
import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

import Icon from "@/components/alice/Icon.vue";
import ThinkingDots from "@/components/alice/ThinkingDots.vue";
import EarlierResearchRow from "@/components/alice/EarlierResearchRow.vue";
import {
  SEARCH_FOLLOWS,
  askAboutPrompt,
  comparePreviousRoundPrompt,
  driveFolderInput,
  driveFolderUrl,
  driveLinkFields,
  driveLinkReason,
  errorMessage,
  fullHistoryPrompt,
  historySummary,
  linkErrorMessage,
  mergeNotes,
  roundLabel,
  testscriptPrompt,
} from "@/utils/earlierResearch";

// Results per project for a few minutes, so going to the chat and back does
// not search again.
const CACHE_MS = 5 * 60 * 1000;
const cache = new Map();

// The two calls the tab makes: the catalog lists (fast) and the similar
// studies (a search, a few seconds), so the first two sections show while
// the third is still searching.
const CALLS = {
  main: { parts: ["related", "history"], error: "errorMain" },
  similar: { parts: ["similar"], error: "errorSimilar" },
};

export default {
  name: "ProjectEarlierResearch",
  components: { Icon, ThinkingDots, EarlierResearchRow },
  data() {
    return {
      // The getEarlierResearch responses, per call.
      res: { main: null, similar: null },
      loading: { main: false, similar: false },
      errorMain: "",
      errorSimilar: "",
      loadedFor: null,
      SEARCH_FOLLOWS,
      // Linking to Drive: the control a call is running for (a row id,
      // "paste", "unlink" or "undo:<archive id>"), and the last error with
      // the control it belongs to.
      linkBusy: null,
      linkError: "",
      linkErrorFor: null,
      pasteOpen: false,
      pasteValue: "",
      // Rows a "not the same study" hid, by archive id, for "Ongedaan maken".
      hiddenRows: {},
    };
  },
  computed: {
    isGuest() {
      return this.$store.getters.isGuest;
    },
    projectId() {
      return this.$route.params.id;
    },
    // The project's Drive link and job number as the store has them (null
    // until this project is loaded): the cached results belong to one value.
    linkKey() {
      const p = this.$store.state.project;
      if (!p || p.uid !== this.projectId) return null;
      return [p.drive_folder_id || "", p.project_code || ""].join("|");
    },
    loadingMain() {
      return this.loading.main;
    },
    loadingSimilar() {
      return this.loading.similar;
    },
    related() {
      return (this.res.main && this.res.main.related) || null;
    },
    history() {
      return (this.res.main && this.res.main.history) || null;
    },
    similar() {
      return (this.res.similar && this.res.similar.similar) || null;
    },
    previousRounds() {
      return (this.related && this.related.previous_rounds) || [];
    },
    relatedCount() {
      const r = this.related;
      if (!r || r.error) return 0;
      return (
        r.previous_rounds.length +
        r.same_study.length +
        r.next_rounds.length +
        (this.driveRow ? 1 : 0)
      );
    },
    // Linking to Drive (getEarlierResearch related.link).
    link() {
      return (this.related && !this.related.error && this.related.link) || null;
    },
    canLink() {
      return !this.isGuest && Boolean(this.link && this.link.can_link);
    },
    rejectedList() {
      return (this.link && this.link.rejected) || [];
    },
    // The Drive study linked by hand, as a row.
    driveRow() {
      const d = this.link && this.link.drive;
      if (!d) return null;
      return {
        id: d.archive_id || d.folder_id,
        title: d.title || "Drive-map",
        client: d.client || (this.history && this.history.client) || null,
        year: Number.isInteger(d.year) ? d.year : null,
        source: "archive",
        ...(d.archive_id ? { archive_id: d.archive_id } : {}),
        links: [{ kind: "folder", url: d.url }, ...(d.links || [])],
        reason: driveLinkReason(d),
      };
    },
    driveNote() {
      const d = this.link && this.link.drive;
      if (!d) return "";
      return d.in_catalog
        ? "Alice en de MCP-tools zoeken dit project en deze Drive-map als één studie."
        : `Je ziet de koppeling nu al hier. ${SEARCH_FOLLOWS}`;
    },
    historyCount() {
      return this.history && !this.history.error && this.history.total
        ? historySummary(this.history)
        : "";
    },
    notes() {
      return mergeNotes(this.res.main, this.res.similar);
    },
    builtLabel() {
      const at =
        (this.res.main && this.res.main.catalog_built_at) ||
        (this.res.similar && this.res.similar.catalog_built_at);
      const d = at ? new Date(at) : null;
      if (!d || Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.linkBusy = null;
        this.clearLinkError();
        this.closePaste();
        this.hiddenRows = {};
        this.load();
      },
    },
  },
  methods: {
    roundLabel,
    // ---- Linking to Drive -------------------------------------------------
    // Linking is linkDriveFolder (it checks the folder in Drive and queues it
    // for the archive sync); "not the same study" is rejectStudyLink. The tab
    // shows a decision at once, the way the server will (getEarlierResearch
    // reads the link and the decisions live); search follows after the
    // nightly catalog refresh.
    folderUrl(row) {
      const folder = (row.links || []).find((l) => l && l.kind === "folder");
      return folder ? folder.url : "";
    },
    clearLinkError() {
      this.linkError = "";
      this.linkErrorFor = null;
    },
    openPaste() {
      this.clearLinkError();
      this.pasteOpen = true;
      this.$nextTick(() => this.$refs.pasteInput?.focus());
    },
    closePaste() {
      this.pasteOpen = false;
      this.pasteValue = "";
      if (this.linkErrorFor === "paste") this.clearLinkError();
    },
    async callFunction(name, data) {
      const call = httpsCallable(functions, name, { timeout: 60000 });
      return (await call(data)).data;
    },
    // Run one link action for control `forId`; false when it failed (the
    // error is shown next to that control).
    async linkAction(forId, run) {
      if (this.linkBusy) return false;
      this.clearLinkError();
      this.linkBusy = forId;
      try {
        await run();
        return true;
      } catch (err) {
        console.error(`Drive link (${forId}):`, err);
        this.linkError = linkErrorMessage(err);
        this.linkErrorFor = forId.startsWith("undo:") ? "undo" : forId;
        return false;
      } finally {
        this.linkBusy = null;
      }
    },
    // The tab after a link made by hand: the link stands for the Drive side,
    // the suggestions go (as getEarlierResearch will say on the next load).
    applyDriveLink(res, row = null) {
      const rel = this.related;
      if (!rel || !res || !res.folder) return;
      rel.same_study = rel.same_study.filter(
        (r) => !r.possible && !r.archive_id
      );
      rel.link = {
        can_link: true,
        rejected: [],
        ...(rel.link || {}),
        drive: {
          folder_id: res.folder.id,
          archive_id: res.archive_project_id || null,
          title: (row && row.title) || res.folder.name || null,
          client: (row && row.client) || res.client_name || null,
          year: row && Number.isInteger(row.year) ? row.year : null,
          url: driveFolderUrl(res.folder.id),
          linked_at: Date.now(),
          in_catalog: false,
          links: ((row && row.links) || []).filter((l) => l.kind !== "folder"),
        },
      };
      this.syncProjectFields(res);
    },
    // A later Save writes the project doc from the store; it must carry the
    // link as linkDriveFolder wrote it.
    syncProjectFields(res) {
      const uid = this.$store.state.user && this.$store.state.user.uid;
      this.$store.commit("setProjectFields", {
        uid: this.projectId,
        fields: driveLinkFields(res, uid),
      });
    },
    confirmLink(row) {
      return this.linkAction(row.id, async () => {
        const res = await this.callFunction("linkDriveFolder", {
          projectId: this.projectId,
          folder: row.folder_id,
        });
        this.applyDriveLink(res, row);
      });
    },
    async pasteLink() {
      const folder = driveFolderInput(this.pasteValue);
      if (!folder) {
        this.linkError =
          "Plak de link van een map op Drive (drive.google.com/drive/folders/…).";
        this.linkErrorFor = "paste";
        return;
      }
      const ok = await this.linkAction("paste", async () => {
        const res = await this.callFunction("linkDriveFolder", {
          projectId: this.projectId,
          folder,
        });
        this.applyDriveLink(res);
      });
      if (ok) this.closePaste();
    },
    unlinkDrive() {
      return this.linkAction("unlink", async () => {
        const res = await this.callFunction("linkDriveFolder", {
          projectId: this.projectId,
          unlink: true,
        });
        if (this.link) this.link.drive = null;
        this.syncProjectFields(res);
      });
    },
    // "Not the same study": a suggestion goes; an automatic link takes the
    // whole Drive side of the study with it (the folder and its
    // sub-folders, and other Alice projects that were the same study
    // through it).
    rejectLink(row) {
      return this.linkAction(row.id, async () => {
        await this.callFunction("rejectStudyLink", {
          projectId: this.projectId,
          archiveProjectId: row.archive_id,
        });
        const rel = this.related;
        const hidden = row.possible
          ? [row]
          : rel.same_study.filter((r) => !r.possible);
        rel.same_study = rel.same_study.filter((r) => !hidden.includes(r));
        this.hiddenRows = { ...this.hiddenRows, [row.archive_id]: hidden };
        if (rel.link) {
          rel.link.rejected = [
            ...rel.link.rejected.filter((x) => x.archive_id !== row.archive_id),
            { archive_id: row.archive_id, title: row.title },
          ];
        }
      });
    },
    undoReject(item) {
      return this.linkAction(`undo:${item.archive_id}`, async () => {
        await this.callFunction("rejectStudyLink", {
          projectId: this.projectId,
          archiveProjectId: item.archive_id,
          undo: true,
        });
        const rel = this.related;
        if (rel.link) {
          rel.link.rejected = rel.link.rejected.filter(
            (x) => x.archive_id !== item.archive_id
          );
        }
        const back = this.hiddenRows[item.archive_id] || [];
        if (back.length) {
          rel.same_study = [...rel.same_study, ...back];
          const rest = { ...this.hiddenRows };
          delete rest[item.archive_id];
          this.hiddenRows = rest;
        }
      });
    },
    load(force = false) {
      const id = this.projectId;
      if (!id || this.isGuest) return;
      this.loadedFor = id;
      let entry = cache.get(id);
      // A Drive link or job number saved since the cached call (on the
      // Details page, or here): ask again. A call made before the project was
      // in the store belongs to the state it was then loaded with.
      const linkKey = this.linkKey;
      if (entry && entry.linkKey === null) entry.linkKey = linkKey;
      const moved =
        Boolean(entry) &&
        linkKey !== null &&
        entry.linkKey !== null &&
        entry.linkKey !== linkKey;
      if (force || moved || !entry || Date.now() - entry.at > CACHE_MS) {
        entry = { at: Date.now(), linkKey };
        cache.set(id, entry);
      }
      const call = httpsCallable(functions, "getEarlierResearch", {
        timeout: 120000,
      });
      // A call still running (the tab was left and opened again) is joined,
      // not repeated.
      const pending = entry.pending || (entry.pending = {});
      for (const [key, { parts, error }] of Object.entries(CALLS)) {
        this[error] = "";
        this.res[key] = entry[key] || null;
        this.loading[key] = !entry[key];
        if (entry[key]) continue;
        if (!pending[key]) {
          pending[key] = call({ projectId: id, parts })
            .then((r) => (entry[key] = r.data))
            .finally(() => delete pending[key]);
        }
        // A newer call for this project (one asked again) wins.
        const current = () => this.loadedFor === id && cache.get(id) === entry;
        pending[key]
          .then((data) => {
            if (current()) this.res[key] = data;
          })
          .catch((err) => {
            console.error(`getEarlierResearch (${parts.join(" + ")}):`, err);
            if (current()) this[error] = errorMessage(err);
          })
          .finally(() => {
            if (current()) this.loading[key] = false;
          });
      }
    },
    // Quick actions: open Project Chat with the prompt in the composer. The
    // colleague reads, adds notes and sends it; nothing is sent from here.
    openChat(text) {
      if (!text) return;
      this.$store.commit("setChatPrefill", text);
      this.$router.push(`/project/${this.projectId}/chat`);
    },
    comparePreviousRound() {
      this.openChat(
        comparePreviousRoundPrompt({ previous: this.previousRounds })
      );
    },
    testscriptBlocks() {
      this.openChat(testscriptPrompt({ previous: this.previousRounds }));
    },
    askAbout(row) {
      this.openChat(askAboutPrompt(row));
    },
    askFullHistory() {
      this.openChat(fullHistoryPrompt(this.history && this.history.client));
    },
  },
};
</script>

<style scoped lang="scss">
.alice-er {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-er__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-er__head-main {
  flex: 1;
  min-width: 0;
}
.alice-er__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-er__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-er__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 600px;
  line-height: 1.5;
}
.alice-er__head-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  max-width: 50%;
}
.alice-er__head-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  border-radius: 0;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover),
    transform var(--dur-press) var(--ease-out);
}
.alice-er__head-btn:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-er__head-btn:active {
  transform: scale(0.98);
}

/* ============== Body ============== */
.alice-er__body {
  display: flex;
  flex-direction: column;
  padding: 8px 32px 48px;
}
.alice-er__empty {
  padding: 32px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-er__note {
  margin: 20px 0 0;
  padding: 10px 14px;
  background: var(--vp-geel-10);
  border-left: 3px solid var(--vp-geel-100);
  font-family: var(--font-ui);
  font-size: 13px;
  line-height: 1.5;
  color: var(--fg-1);
}

/* ============== Section ============== */
.alice-er__section {
  border-bottom: 1px solid var(--border-hairline);
  padding: 28px 0 24px;
  display: flex;
  flex-direction: column;
}
.alice-er__section:last-of-type {
  border-bottom: none;
}
.alice-er__section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.alice-er__section-mark {
  width: 12px;
  height: 12px;
  background: var(--vp-black);
  flex-shrink: 0;
}
.alice-er__section-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}
.alice-er__section-sub {
  flex-basis: 100%;
  margin: 0 0 0 24px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
  line-height: 1.5;
}
.alice-er__count-pill {
  padding: 2px 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--fg-3);
  border: 1px solid var(--border-hairline);
}

/* ============== States ============== */
.alice-er__loading,
.alice-er__none,
.alice-er__error {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 4px 24px;
  font-family: var(--font-ui);
  font-size: 13px;
  line-height: 1.5;
  color: var(--fg-3);
}
.alice-er__error {
  color: var(--vp-rood-120);
}
.alice-er__retry,
.alice-er__more-btn {
  height: 28px;
  padding: 0 10px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vp-black);
}
.alice-er__retry:hover,
.alice-er__more-btn:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-er__more {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0 0;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-er__seed {
  margin: 14px 0 0;
  font-family: var(--font-ui);
  font-size: 12px;
  line-height: 1.5;
  color: var(--fg-3);
}
.alice-er__foot {
  padding-top: 16px;
  font-family: var(--font-ui);
  font-size: 12px;
  line-height: 1.5;
  color: var(--fg-3);
}

/* ============== Linking to Drive ============== */
.alice-er-link {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 8px;
}
.alice-er-link--ask {
  padding: 8px 10px;
  background: var(--vp-turquoise-10);
  border-left: 3px solid var(--vp-turquoise-100);
}
.alice-er-link__text {
  font-family: var(--font-ui);
  font-size: 13px;
  line-height: 1.5;
  color: var(--fg-1);
}
.alice-er-link__folder {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 700;
  color: var(--vp-black);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.alice-er-link__actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
}
.alice-er-link__btn {
  height: 28px;
  padding: 0 10px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vp-black);
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover);
}
.alice-er-link__btn:hover:not(:disabled) {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-er-link__btn--yes {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-er-link__btn--yes:hover:not(:disabled) {
  background: var(--vp-turquoise-160);
  border-color: var(--vp-turquoise-160);
}
.alice-er-link__btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-er-link__quiet {
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}
.alice-er-link__quiet:hover:not(:disabled) {
  color: var(--vp-black);
}
.alice-er-link__quiet:disabled {
  cursor: default;
}
.alice-er-link__hint {
  flex-basis: 100%;
  font-family: var(--font-ui);
  font-size: 12px;
  line-height: 1.5;
  color: var(--fg-3);
}
.alice-er-link__error {
  margin: 6px 0 0;
  font-family: var(--font-ui);
  font-size: 12px;
  line-height: 1.5;
  color: var(--vp-rood-120);
}
.alice-er-link__undo {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  margin: 12px 0 0;
  padding: 8px 12px;
  background: var(--vp-grijs-10);
  font-family: var(--font-ui);
  font-size: 13px;
  line-height: 1.5;
  color: var(--fg-2);
}
.alice-er-paste {
  margin-top: 16px;
}
.alice-er-paste__form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 760px;
}
.alice-er-paste__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
}
.alice-er-paste__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.alice-er-paste__row .alice-er-link__btn {
  height: 32px;
}
.alice-er-paste__input {
  flex: 1 1 320px;
  min-width: 0;
  height: 32px;
  padding: 0 10px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--vp-black);
}
.alice-er-paste__input:focus {
  outline: 2px solid var(--vp-turquoise-100);
  outline-offset: 1px;
}
.alice-er-paste__hint {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  line-height: 1.5;
  color: var(--fg-3);
}

@media (max-width: 1279px) {
  .alice-er__head {
    flex-direction: column;
  }
  .alice-er__head-actions {
    max-width: none;
    justify-content: flex-start;
  }
}
</style>
